import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../constants';

const renderMessageText = (text) => {
  const urlRegex = /(https?:\/\/[^\s]+)/g;
  const parts = text.split(urlRegex);
  return parts.map((part, i) => {
    if (urlRegex.test(part)) {
      urlRegex.lastIndex = 0;
      const label = part.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer"
          className="underline font-bold hover:opacity-80 transition-opacity"
          data-testid="chat-link"
          style={{ wordBreak: 'break-all' }}>
          {label}
        </a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const HostessChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [sessionId] = useState(() => 'chat_' + Math.random().toString(36).slice(2, 10));
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);

  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{ role: 'ai', text: 'Ciao! Sono </ , la tua guida digitale. Parla pure, ti ascolto. Premi il microfono o scrivi.' }]);
    }
    if (open && inputRef.current) inputRef.current.focus();
  }, [open, messages.length]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/chat`, { message: text, session_id: sessionId });
      setMessages(prev => [...prev, { role: 'ai', text: res.data.reply }]);
    } catch {
      setMessages(prev => [...prev, { role: 'ai', text: 'Mi dispiace, riprova tra un attimo.' }]);
    }
    setLoading(false);
  };

  const fileExtRef = useRef('webm');

  const toggleRecording = async () => {
    if (recording) {
      // STOP recording
      if (mediaRecRef.current) {
        mediaRecRef.current.stop();
        setRecording(false);
      }
      return;
    }
    // START recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      // Detect best supported MIME — iOS Safari needs mp4/m4a
      let mimeType = '';
      const candidates = [
        'audio/mp4',
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/wav',
      ];
      for (const c of candidates) {
        if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(c)) {
          mimeType = c;
          break;
        }
      }
      if (!mimeType) mimeType = ''; // let browser pick default

      fileExtRef.current = mimeType.includes('mp4') ? 'mp4' : mimeType.includes('ogg') ? 'ogg' : mimeType.includes('wav') ? 'wav' : 'webm';

      const recOptions = mimeType ? { mimeType } : undefined;
      const mediaRec = new MediaRecorder(stream, recOptions);
      mediaRecRef.current = mediaRec;
      chunksRef.current = [];
      mediaRec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const actualMime = mediaRec.mimeType || mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: actualMime });
        if (blob.size < 100) {
          setMessages(prev => [...prev, { role: 'ai', text: 'Non ho sentito nulla. Tocca il microfono, parla, e tocca di nuovo per inviare.' }]);
          return;
        }
        setMessages(prev => [...prev, { role: 'user', text: 'Nota vocale in ascolto...' }]);
        setLoading(true);
        try {
          const ext = fileExtRef.current;
          const form = new FormData();
          form.append('audio', blob, `voice.${ext}`);
          form.append('session_id', sessionId);
          const res = await axios.post(`${API}/chat/voice`, form, { timeout: 30000 });
          setMessages(prev => {
            const updated = [...prev];
            const lastUser = updated.findLastIndex(m => m.role === 'user');
            if (lastUser >= 0 && res.data.transcription) {
              updated[lastUser] = { role: 'user', text: `"${res.data.transcription}"` };
            } else if (lastUser >= 0 && !res.data.transcription) {
              updated[lastUser] = { role: 'user', text: '(nota vocale)' };
            }
            return [...updated, { role: 'ai', text: res.data.reply }];
          });
        } catch (err) {
          console.error('Voice error:', err);
          setMessages(prev => [...prev, { role: 'ai', text: 'Ho avuto un problema con l\'audio. Puoi scrivermi qui sotto? Ti ascolto con la stessa attenzione, promesso.\n\nOppure WhatsApp: https://wa.me/393273839782' }]);
        }
        setLoading(false);
      };
      mediaRec.start(1000);
      setRecording(true);
    } catch (err) {
      console.error('Mic error:', err);
      setMessages(prev => [...prev, {
        role: 'ai',
        text: 'Il tuo browser non mi permette di ascoltarti. Su iPhone: vai su Impostazioni > Safari > Microfono e consenti. Oppure usa Chrome.\n\nSe non riesci, scrivimi qui — ti ascolto anche in testo!\n\nOppure nota vocale su WhatsApp: https://wa.me/393273839782'
      }]);
    }
  };

  const [donaHidden, setDonaHidden] = useState(() => sessionStorage.getItem('dona_hidden') === '1');

  const hideDona = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDonaHidden(true);
    sessionStorage.setItem('dona_hidden', '1');
  };

  return (
    <>
      {/* CTA DONA sopra il microfono */}
      {!open && !donaHidden && (
        <div className="fixed bottom-24 left-6 z-[9998]">
          <Link
            to="/dona"
            data-testid="floating-dona-cta"
            className="flex items-center gap-1.5 px-4 py-2 rounded-full font-mono font-black text-xs transition-all hover:scale-105"
            style={{
              background: '#00FF41',
              color: '#000',
              boxShadow: '0 0 15px rgba(0,255,65,0.3)',
            }}
          >
            &lt;/ DONA
          </Link>
          <button
            onClick={hideDona}
            data-testid="dona-close-btn"
            className="absolute -top-1.5 -right-1.5 z-10 w-4 h-4 flex items-center justify-center rounded-full text-[7px] font-bold"
            style={{ background: '#FF2D2D', color: '#fff', border: '1.5px solid #fff' }}
          >X</button>
        </div>
      )}
      <button
        data-testid="hostess-fab"
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 left-6 z-[9999] w-16 h-16 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110"
        style={{
          background: open ? '#00FF41' : '#0A0A0A',
          border: '2px solid #00FF41',
          boxShadow: '0 0 20px rgba(0,255,65,0.3), 0 0 40px rgba(0,255,65,0.1)',
          animation: open ? 'none' : 'hostessPulse 2s ease-in-out infinite',
        }}
        title="</  ti ascolta!"
      >
        {open ? (
          <span className="text-black font-black text-xl">✕</span>
        ) : (
          <div className="relative flex items-center justify-center">
            <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none">
              <defs>
                <linearGradient id="tricolore" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#009246"/>
                  <stop offset="33%" stopColor="#009246"/>
                  <stop offset="33%" stopColor="#FFFFFF"/>
                  <stop offset="66%" stopColor="#FFFFFF"/>
                  <stop offset="66%" stopColor="#CE2B37"/>
                  <stop offset="100%" stopColor="#CE2B37"/>
                </linearGradient>
              </defs>
              <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z" fill="url(#tricolore)"/>
              <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z" fill="url(#tricolore)"/>
            </svg>
            <span className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-red-500 animate-pulse flex items-center justify-center" style={{ boxShadow: '0 0 8px rgba(239,68,68,0.6)' }}>
              <span className="font-mono font-black text-white text-[6px] leading-none" style={{ letterSpacing: '1px' }}>&lt;/</span>
            </span>
          </div>
        )}
      </button>

      {open && (
        <div
          data-testid="hostess-chat-panel"
          className="fixed bottom-24 left-3 right-3 sm:left-6 sm:right-auto sm:w-[360px] z-[9999] max-h-[520px] flex flex-col bg-[#0A0A0A] border border-[#00FF41]/30 rounded-lg shadow-2xl overflow-hidden"
          style={{ boxShadow: '0 0 30px rgba(0,255,65,0.15)' }}
        >
          <div className="px-4 py-3 border-b border-[#00FF41]/20 flex items-center gap-3 bg-black/50">
            <img src="/hostess_infopoint.png" alt="Hostess AI" className="w-10 h-10 rounded-full object-cover border border-[#00FF41]/40" />
            <div>
              <p className="font-mono font-bold text-[#00FF41] text-sm">&lt;/ ti ascolta!</p>
              <p className="text-zinc-500 text-xs">Info Point Digitale AI_COVO</p>
            </div>
            <Link to="/about" onClick={() => setOpen(false)} className="ml-auto font-mono text-[10px] font-bold text-black bg-[#00FF41] px-2.5 py-1 rounded-full hover:bg-[#33FF66] transition-colors">
              All About Us
            </Link>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" style={{ maxHeight: '340px' }}>
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] px-3 py-2 rounded-lg text-sm ${
                  m.role === 'user'
                    ? 'bg-[#00FF41]/10 text-[#00FF41] border border-[#00FF41]/20'
                    : 'bg-zinc-900 text-zinc-200 border border-zinc-800'
                }`}>
                  {renderMessageText(m.text)}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 rounded-lg">
                  <span className="text-[#00FF41] text-sm animate-pulse font-mono">&lt;/ sta ascoltando...</span>
                </div>
              </div>
            )}
          </div>

          <div className="px-3 py-3 border-t border-zinc-800 bg-black/30">
            {recording && (
              <div className="flex items-center justify-center gap-2 mb-2 py-2 bg-red-500/10 border border-red-500/30 rounded-lg">
                <span className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                <span className="text-red-400 text-xs font-mono animate-pulse">Sto ascoltando... tocca il mic per inviare</span>
              </div>
            )}
            <div className="flex gap-2">
              <input
                ref={inputRef}
                data-testid="hostess-chat-input"
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Scrivi o tocca il mic..."
                className="flex-1 bg-zinc-900 border border-zinc-700 rounded-full px-4 py-2 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-[#00FF41]/50"
              />
              <button
                data-testid="hostess-voice-btn"
                onClick={toggleRecording}
                disabled={loading}
                className={`w-11 h-11 rounded-full flex items-center justify-center transition-all disabled:opacity-30 flex-shrink-0 ${
                  recording ? 'bg-red-500 scale-110 animate-pulse ring-2 ring-red-400/50' : 'bg-zinc-800 hover:bg-zinc-700 border border-[#00FF41]/40 hover:border-[#00FF41]'
                }`}
                title={recording ? "Tocca per inviare" : "Tocca per parlare"}
              >
                {recording ? (
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-[#00FF41]" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                    <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                  </svg>
                )}
              </button>
              <button
                data-testid="hostess-chat-send"
                onClick={send}
                disabled={loading || !input.trim()}
                className="w-9 h-9 rounded-full bg-[#00FF41] flex items-center justify-center hover:bg-[#33FF66] transition-colors disabled:opacity-30"
              >
                <span className="font-mono font-black text-black text-sm" style={{ letterSpacing: '1px' }}>&lt; /</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HostessChat;
